import * as Sentry from '@sentry/react'
import { AppLayout } from '@shared/components/AppLayout'
import { RouteErrorBoundary } from '@shared/components/ErrorFallback'
import { LoadingScreen } from '@shared/components/LoadingScreen'
import { lazy, Suspense } from 'react'
import { createBrowserRouter, type RouteObject } from 'react-router-dom'

import { ProtectedRoute } from './ProtectedRoute'
import { ROUTES } from './routes'

/**
 * Lazy-loaded route components for code splitting
 */
const Dashboard = lazy(() =>
  import('@features/dashboard/components/Dashboard').then((m) => ({ default: m.Dashboard })),
)
const TenantList = lazy(() =>
  import('@features/tenants/components/TenantList').then((m) => ({ default: m.TenantList })),
)
const TenantDetail = lazy(() =>
  import('@features/tenants/components/TenantDetail').then((m) => ({ default: m.TenantDetail })),
)
const Profile = lazy(() => import('@pages/Profile').then((m) => ({ default: m.Profile })))
const Theme = lazy(() => import('@pages/Theme').then((m) => ({ default: m.Theme })))
const NotFound = lazy(() =>
  import('@shared/components/NotFound').then((m) => ({ default: m.NotFound })),
)

/** Wrap a lazy-loaded route element with the shared Suspense fallback. */
const withSuspense = (element: React.ReactNode): React.ReactNode => (
  <Suspense fallback={<LoadingScreen />}>{element}</Suspense>
)

/**
 * RR7 data-router configuration.
 *
 * - Root `errorElement` catches any error thrown during route rendering and
 *   reports it to Sentry while keeping the rest of the app shell alive.
 * - Protected routes sit under `ProtectedRoute`, which gates on Keycloak.
 * - Each protected route is wrapped in Suspense so its own code-split chunk
 *   is allowed to stream in without blocking sibling routes.
 */
const routeObjects: RouteObject[] = [
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: ROUTES.DASHBOARD, element: withSuspense(<Dashboard />) },
          { path: ROUTES.TENANTS, element: withSuspense(<TenantList />) },
          { path: ROUTES.TENANT_DETAIL, element: withSuspense(<TenantDetail />) },
          { path: ROUTES.PROFILE, element: withSuspense(<Profile />) },
          { path: ROUTES.THEME, element: withSuspense(<Theme />) },
        ],
      },
    ],
  },
  // Catch-all renders NotFound inline; no redirect so the URL the user
  // typed is preserved (useful for bug reports and deep links).
  { path: '*', element: withSuspense(<NotFound />) },
]

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

export const router = sentryCreateBrowserRouter(routeObjects)
